/**
 * Provider help center on its own host: `docs.<staff apex>` (local: `docs.localhost`).
 * Pure strings, shared by Next middleware, routes and client bundles.
 */

import { normalizeHostname } from "../portal/portal_host";
import { PRODUCTION_STAFF_APEX, STAFF_APEX_DOMAIN, isStaffHostname } from "./staff_host";

export const DOCS_HOST_LABEL = "docs";

/** Route slug the docs host rewrites to; reserved label, so never an org. */
export const DOCS_HELP_SLUG = "docs";

/** Set by middleware on docs-host requests; stripped from client requests. */
export const DOCS_REQUEST_HEADER = "x-help-docs";

export function isDocsHost(host: string | null | undefined): boolean {
  const hostname = normalizeHostname(host);
  return (
    hostname === `${DOCS_HOST_LABEL}.${PRODUCTION_STAFF_APEX}` ||
    hostname === `${DOCS_HOST_LABEL}.${STAFF_APEX_DOMAIN}` ||
    hostname === `${DOCS_HOST_LABEL}.localhost`
  );
}

/** Docs origin next to `appOrigin`; null where no docs host exists (Vercel previews). */
export function docsOriginFor(appOrigin: string): string | null {
  let url: URL;
  try {
    url = new URL(appOrigin);
  } catch {
    return null;
  }
  if (url.hostname === "localhost") {
    return `${url.protocol}//${DOCS_HOST_LABEL}.localhost${url.port ? `:${url.port}` : ""}`;
  }
  return isStaffHostname(url.hostname)
    ? `https://${DOCS_HOST_LABEL}.${STAFF_APEX_DOMAIN}`
    : null;
}

/** App origin behind a docs origin, for sign-in and dashboard links. */
export function appOriginOfDocs(docsOrigin: string): string {
  return docsOrigin.replace(`//${DOCS_HOST_LABEL}.`, "//");
}

export type DocsHostRoute =
  | { readonly kind: "pass" }
  | { readonly kind: "rewrite"; readonly pathname: string }
  /** Old `/help/<org>/…` paths belong to the app host, which redirects onward. */
  | { readonly kind: "app_host" };

/** Docs host: every page path is the provider help center at the host root. */
export function docsHostRoute(pathname: string): DocsHostRoute {
  const segments = pathname.split("/").filter(Boolean);
  const last = segments[segments.length - 1] ?? "";
  // Files (robots.txt, sitemap/0.xml) and framework paths: page ids carry no dot.
  if (last.includes(".") || segments[0]?.startsWith("_")) return { kind: "pass" };
  if (segments[0] === "help") return { kind: "app_host" };
  const rest = segments.length === 0 ? "" : `/${segments.join("/")}`;
  return { kind: "rewrite", pathname: `/help/${DOCS_HELP_SLUG}${rest}` };
}

/** Path below `/help/<slug>` (leading slash or empty), for the redirect to the docs host. */
export function helpSubpath(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] !== "help" || segments.length < 2) return "";
  const rest = segments.slice(2);
  return rest.length === 0 ? "" : `/${rest.join("/")}`;
}
